import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { cloudRouteFetcher } from '../map/publicRoute.ts';
import { SymbolGlyph } from '../map/SymbolGlyph.tsx';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  blockFeatures,
  BOUNDARY_LEGEND,
  canDeleteProject,
  canPortal,
  deleteConfirmMatches,
  DIRECTION_ARROW_LEGEND,
  isProjectStatus,
  kmText,
  clientVisiblePhotos,
  lifecycleDateText,
  linesRemaining,
  livePhotos,
  photoFeatures,
  projectProgress,
  projectStatusLabel,
  PROJECT_STATUSES,
  PORTAL_REPLAY_HOURS,
  PORTAL_REPLAY_NOTE,
  REMAINING_LEGEND,
  REMAINING_MAP_STATES,
  statusChoices,
  suggestComplete,
  type AircraftPosition,
  type LegendEntry,
  type LineState,
  type TrailPoint,
  withInfillKm,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { Tabs } from '../components/Tabs.tsx';
import {
  deleteProject,
  loadClientOrgs,
  loadProjectPlan,
  loadProjects,
  projectHasFieldData,
  setProjectClientOrg,
  setProjectStatus,
  type OrgOption,
  type PortalProject,
  type ProjectPlan,
  type Scope,
} from './api.ts';
import { OverviewPanel } from './OverviewPanel.tsx';
import { RemainingMap, type RemainingMapTracking } from './RemainingMap.tsx';
import { ReplayBar, useReplay } from '../map/ReplayBar.tsx';
import {
  ClientMapSettings,
  PORTAL_MAP_SHOWN,
  PortalMapControls,
  PortalTrackingList,
  usePortalMap,
  viewerOffsetH,
  type PortalMapShown,
} from './PortalMapPanel.tsx';

/** Stable empties for the replay while the client's map has no aircraft yet. */
const NO_AIRCRAFT: readonly AircraftPosition[] = [];
const NO_TRAILS: ReadonlyMap<string, readonly TrailPoint[]> = new Map();
import { FireControls, useCloudFires, useFireChoice } from '../map/cloudFires.tsx';
import { useProjectAircraft } from './projectTraffic.ts';
import { ReportsList, type ReportsListProps, type ReportsMode } from './ReportsList.tsx';
import { FuelDocketsList } from './FuelDocketsList.tsx';
import { DocumentsPanel } from './DocumentsPanel.tsx';
import { PhotosTab, type PhotoServices } from './PhotosTab.tsx';
import { ReflyTab } from './ReflyTab.tsx';
import { SafetyTab } from '../deliverables/SafetyTab.tsx';
import { ApprovalsTab } from '../approvals/ApprovalsTab.tsx';
import { DiurnalTab } from './DiurnalTab.tsx';
import { loadPhotos, type PortalPhoto } from './photosApi.ts';
import { ProjectCrewPanel } from '../staff/ProjectCrewPanel.tsx';
import { ProjectFlights } from '../flights/ProjectFlights.tsx';
import { DeletedProjectsScreen } from './DeletedProjectsScreen.tsx';
import { SetupWizard } from '../setup/SetupWizard.tsx';
import { ProjectDetailsPanel } from '../setup/ProjectDetailsPanel.tsx';
import { BriefSettingPanel } from '../deliverables/BriefSettingPanel.tsx';
import { FinalReportTextPanel } from '../finalReport/FinalReportTextPanel.tsx';
import type { PageId } from '../access/roles.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export type ProjectTab =
  | 'progress'
  | 'map'
  | 'reports'
  | 'refly'
  | 'diurnal'
  | 'photos'
  | 'safety'
  | 'approvals'
  | 'flights'
  | 'dockets'
  | 'crew'
  | 'setup'
  | 'final-report';

/** The project list's views: today's projects, or (tenant admins) the Deleted list (C4, decision 844). */
export type ProjectsView = 'current' | 'deleted';

const PROJECT_TABS: readonly string[] = [
  'progress',
  'map',
  'reports',
  'refly',
  'diurnal',
  'photos',
  'safety',
  'approvals',
  'flights',
  'dockets',
  'crew',
  'setup',
  'final-report',
] satisfies readonly ProjectTab[];

/** A tab name from a link: one of a project's tabs, else the first (Overview for staff, Progress for a client). */
export function projectTabOf(v: string | null | undefined): ProjectTab {
  return v && PROJECT_TABS.includes(v) ? (v as ProjectTab) : 'progress';
}

export interface ProjectsScreenProps {
  readonly sb: SupabaseClient;
  readonly scope: Scope;
  /** Client view (client users and Preview as client) or the staff workspace. */
  readonly mode: ReportsMode;
  /** The tab a project opens on (Overview or, for a client, Progress; a link to the old Flights page opens Flights). */
  readonly initialTab?: ProjectTab;
  /** The project open at first (a client notice's link, `?page=projects&project=<id>`); one not listed opens none. */
  readonly initialProjectId?: string | null;
  /** Tenant admins: the list opens on the Deleted list (a link to the old Deleted projects page). */
  readonly initialView?: ProjectsView;
  /** Staff only: open the client organisation in Clients, the contract in Contracts. */
  readonly onOpenClients?: () => void;
  readonly onOpenContract?: (contractId: string) => void;
  /** Staff only: open another page from the Set-up tab (People, Aircraft, Station health, Alerts, Clients). */
  readonly onOpenPage?: (page: PageId) => void;
  /** The pages this person may open (the Set-up tab shows only those buttons). */
  readonly openablePages?: readonly PageId[];
  /** Staff roles, for the publish controls (display only). */
  readonly roles?: readonly string[];
  /** The client's role in the organisation (client_admin approves, R191); in a preview, the role previewed. */
  readonly clientRole?: string | null;
  /** Staff only: the organisation's slug (the final report's starting text is offered to Thomson Airborne). */
  readonly tenantSlug?: string;
  /** Called after a report PDF is opened (Preview as client audits it). */
  readonly onReportOpened?: ReportsListProps['onOpened'];
  /** The open project's tab, "deleted" on the Deleted list, or null on the list (the help panel follows it). */
  readonly onTabChange?: (tab: ProjectTab | 'deleted' | null) => void;
  /** Photo image work and archive (tests inject fakes). */
  readonly photoServices?: PhotoServices;
  /**
   * Set while a staff member previews the client view: the empty list then says, to the staff member, that no project
   * is linked to the organisation and where to link one (MB's R99). The data shown is the client's exactly.
   */
  readonly preview?: boolean;
}

/** Hook: load once per key, with retry. */
function useLoad<T>(load: () => Promise<T>): {
  readonly data: T | null;
  readonly error: string | null;
  readonly reload: () => void;
  /** Read again keeping what is shown meanwhile (an open project stays on its tab). */
  readonly refresh: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(() => {
    let live = true;
    setError(null);
    setData(null);
    load()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [load]);
  useEffect(run, [run]);
  const refresh = useCallback(() => {
    load()
      .then((d) => setData(d))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [load]);
  return { data, error, reload: run, refresh };
}

/** Projects of the scope; opening one shows its Overview (a client: its progress), map and reports. */
export function ProjectsScreen(props: ProjectsScreenProps): ReactElement {
  const { sb, scope, mode, onTabChange } = props;
  const roles = mode === 'staff' ? (props.roles ?? []) : [];
  const [open, setOpen] = useState<string | null>(props.initialProjectId ?? null);
  // C4 (decision 844): the Deleted list is a view of Projects for tenant admins, as the Deleted projects page was
  const binOpen = mode === 'staff' && scope.kind === 'tenant' && roles.includes('tenant_admin');
  const [view, setView] = useState<ProjectsView>(binOpen ? (props.initialView ?? 'current') : 'current');
  useEffect(() => {
    if (!open) onTabChange?.(view === 'deleted' ? 'deleted' : null);
  }, [open, view, onTabChange]);
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const scopeKey = scope.kind === 'client' ? `c:${scope.clientOrgId}` : `t:${scope.tenantId}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the scope is identified by its key
  const load = useCallback(() => loadProjects(sb, scope), [sb, scopeKey]);
  const { data: projects, error, reload, refresh } = useLoad(load);

  const changeStatus = async (p: PortalProject, status: string): Promise<void> => {
    setFailed(null);
    try {
      await setProjectStatus(sb, p.id, status);
      reload();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    }
  };
  const changeClientOrg = async (p: PortalProject, clientOrgId: string | null): Promise<void> => {
    setFailed(null);
    try {
      await setProjectClientOrg(sb, p.id, clientOrgId);
      reload();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e));
    }
  };

  const project = projects?.find((p) => p.id === open) ?? null;
  if (project) {
    return (
      <ProjectPage
        {...props}
        project={project}
        onBack={() => setOpen(null)}
        onStatus={(s) => void changeStatus(project, s)}
        onClientOrg={(id) => void changeClientOrg(project, id)}
        onDetailsSaved={refresh}
        statusError={failed}
        onDeleted={(text) => {
          setOpen(null);
          setNotice(text);
          reload();
        }}
      />
    );
  }
  const viewControl = binOpen && (
    <SelectField<ProjectsView>
      label="Show"
      value={view}
      onChange={(v) => {
        setView(v);
        setNotice(null);
        // a project restored in the Deleted list shows again in the list
        if (v === 'current') reload();
      }}
      options={[
        ['current', 'Projects'],
        ['deleted', 'Deleted (kept 30 days)'],
      ]}
    />
  );
  if (view === 'deleted' && scope.kind === 'tenant') {
    return (
      <section className="page">
        <PageHeader title="Projects" subtitle="Deleted projects: restore one within 30 days." />
        <div className="row row--wrap projects__view">{viewControl}</div>
        <DeletedProjectsScreen sb={sb} tenantId={scope.tenantId} embedded />
      </section>
    );
  }
  const archived = projects?.filter((p) => p.status === 'archived').length ?? 0;
  const shown = projects?.filter((p) => showArchived || p.status !== 'archived') ?? null;
  return (
    <section className="page">
      <PageHeader
        title="Projects"
        subtitle={
          mode === 'client'
            ? 'Survey projects linked to your organisation.'
            : 'Every survey project of the company, as Teslyn Field syncs it.'
        }
      />
      {notice && (
        <div className="notice" role="status">
          <p className="body">{notice}</p>
        </div>
      )}
      {failed && (
        <div className="notice notice--fail" role="alert">
          <p className="body">{failed}</p>
        </div>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!projects && !error && <Loading what="projects" />}
      {(viewControl || (mode === 'staff' && archived > 0)) && (
        <div className="row row--wrap projects__view">
          {viewControl}
          {mode === 'staff' && archived > 0 && (
            <CheckField
              label={`Show archived (${archived})`}
              checked={showArchived}
              onChange={setShowArchived}
            />
          )}
        </div>
      )}
      {shown && shown.length === 0 && (
        <div className="panel">
          <p className="body">
            {mode === 'client'
              ? 'No projects are linked to your organisation yet. Your survey company links each project when it is set up.'
              : 'No projects yet. A project appears here when Teslyn Field links it to this organisation and syncs.'}
          </p>
          {mode === 'client' && props.preview && (
            <p className="body" role="note">
              Preview: no project is linked to this client organisation, so its portal shows nothing, whatever
              is published or shared. Open the project in Projects and choose the organisation under
              &ldquo;Client organisation&rdquo;; it then appears here.
            </p>
          )}
        </div>
      )}
      {shown && shown.length > 0 && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Project</th>
                <th className="label">Title</th>
                {mode === 'staff' && <th className="label">Client</th>}
                <th className="label">Status</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td className="data">{p.number}</td>
                  <td>{p.title}</td>
                  {mode === 'staff' && <td>{p.clientOrgName ?? 'No client organisation'}</td>}
                  <td>
                    <StatusControl project={p} roles={roles} onChange={(s) => void changeStatus(p, s)} />
                  </td>
                  <td className="table__actions">
                    <Button variant="quiet" aria-label={`Open ${p.number}`} onClick={() => setOpen(p.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

/**
 * The client organisation whose portal shows the project (R99): the tenant's organisations, or none. Display only:
 * policy project_write decides (tenant admins, managers, office staff), as `canPortal(roles, 'project.link_client')`.
 */
function ClientOrgControl({
  sb,
  project,
  onChange,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly onChange: (clientOrgId: string | null) => void;
}): ReactElement {
  const load = useCallback(() => loadClientOrgs(sb, project.tenantId), [sb, project.tenantId]);
  const { data: orgs, error } = useLoad(load);
  const NONE = '';
  const known: OrgOption[] = orgs ?? [];
  // the linked organisation stays choosable while the list loads, or if it was deleted in Clients
  const listed =
    project.clientOrgId && !known.some((o) => o.id === project.clientOrgId)
      ? [{ id: project.clientOrgId, name: project.clientOrgName ?? 'Linked organisation' }, ...known]
      : known;
  const options: ReadonlyArray<readonly [string, string]> = [
    [NONE, 'No client organisation'],
    ...listed.map((o) => [o.id, o.name] as const),
  ];
  return (
    <SelectField<string>
      label="Client organisation"
      value={project.clientOrgId ?? NONE}
      onChange={(v) => onChange(v === NONE ? null : v)}
      options={options}
      {...(error ? { error } : {})}
    />
  );
}

/**
 * The project's status, with a menu of the statuses these roles may set (decisions 416, 423; display only: the
 * database checks every change and audits it). In the Projects table it is a bare list (the column names it); on a
 * project's page (`labelled`) it is the shared field with "Status" above its box, level with Client organisation
 * beside it (R208, decision 1030; docs/specs/ui-layout-rules.md rule 1).
 */
function StatusControl({
  project,
  roles,
  onChange,
  labelled = false,
}: {
  readonly project: PortalProject;
  readonly roles: readonly string[];
  readonly onChange: (status: string) => void;
  readonly labelled?: boolean;
}): ReactElement {
  const current = project.status;
  const choices = isProjectStatus(current) ? statusChoices(roles, current) : [];
  const statuses = PROJECT_STATUSES.filter((s) => s === current || choices.includes(s));
  if (labelled) {
    if (choices.length === 0)
      return (
        <div className="field">
          <span className="label muted">Status</span>
          <p className="body field__value">{projectStatusLabel(current)}</p>
        </div>
      );
    return (
      <SelectField<string>
        label="Status"
        value={current}
        onChange={onChange}
        options={statuses.map((s) => [s, projectStatusLabel(s)] as const)}
      />
    );
  }
  if (choices.length === 0) return <span>{projectStatusLabel(current)}</span>;
  return (
    <select
      className="input"
      aria-label={`Status of ${project.number}`}
      value={current}
      onChange={(e) => onChange(e.target.value)}
    >
      {statuses.map((s) => (
        <option key={s} value={s}>
          {projectStatusLabel(s)}
        </option>
      ))}
    </select>
  );
}

function ProjectPage({
  sb,
  mode,
  project,
  initialTab = 'progress',
  onBack,
  onOpenClients,
  onOpenContract,
  onOpenPage,
  openablePages,
  roles,
  clientRole = null,
  preview = false,
  tenantSlug = '',
  onReportOpened,
  onTabChange,
  photoServices,
  onStatus,
  onClientOrg,
  onDetailsSaved,
  statusError,
  onDeleted,
}: ProjectsScreenProps & {
  readonly project: PortalProject;
  readonly onBack: () => void;
  readonly onStatus: (status: string) => void;
  readonly onClientOrg: (clientOrgId: string | null) => void;
  /** R209: the project details were saved; the list is read again so the heading shows them. */
  readonly onDetailsSaved: () => void;
  readonly statusError: string | null;
  readonly onDeleted: (notice: string) => void;
}): ReactElement {
  // a tab this person has not got (Flights for a client) opens on Progress
  const [tab, setTab] = useState<ProjectTab>(() =>
    mode !== 'staff' && ['flights', 'dockets', 'crew', 'setup', 'final-report'].includes(initialTab)
      ? 'progress'
      : initialTab,
  );
  useEffect(() => {
    onTabChange?.(tab);
  }, [tab, onTabChange]);
  const load = useCallback(
    () => loadProjectPlan(sb, project.id, mode === 'staff' ? 'staff' : 'client'),
    [sb, project.id, mode],
  );
  const { data: plan, error, reload } = useLoad(load);
  const [contract, setContract] = useState<{ id: string; number: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const staffRoles = mode === 'staff' ? (roles ?? []) : [];
  // decision 416: suggested to managers when every line is accepted, never set by itself
  const complete =
    plan !== null &&
    staffRoles.includes('manager') &&
    !['complete', 'archived'].includes(project.status) &&
    suggestComplete(plan.statuses, plan.plan);

  useEffect(() => {
    if (mode !== 'staff' || !onOpenContract) return;
    let live = true;
    void Promise.resolve(sb.from('contract').select('id, number').eq('project_id', project.id).limit(1)).then(
      (r) => {
        const row = (r.data as { id: string; number: string }[] | null)?.[0];
        if (live && row) setContract(row);
      },
    );
    return () => {
      live = false;
    };
  }, [sb, mode, project.id, onOpenContract]);

  const tabs: ReadonlyArray<readonly [ProjectTab, string]> = [
    // R246: staff have Teslyn Field's Overview here; a client keeps Progress, which holds no staff data
    ['progress', mode === 'staff' ? 'Overview' : 'Progress'],
    ['map', 'Map'],
    ['reports', 'Reports'],
    // R24 (decision 503): staff see it as it is now; the client sees the list a manager or office staff published
    ['refly', 'Refly list'],
    // parity C3 (02-060): staff see each day Teslyn Cloud worked out; the client sees the days shown to them
    ['diurnal', 'Diurnal QC'],
    ['photos', 'Photos'],
    // R162 (decision 926): the risk assessment, signed JSA and JSP and the hazards KML, shown to the client per document;
    // "Safety documents" since decision 1037 (the register entries are on the client's Safety register page, R220)
    ['safety', 'Safety documents'],
    // R191 (decision 1010 item 2): block changes, the JSA, JSP and risk assessment, contracts sent for the client's approval
    ['approvals', 'Approvals'],
    // staff only (C8, decision 844: the Flights page became this tab; clients never see flights, RLS refuses them)
    ...(mode === 'staff' ? ([['flights', 'Flights']] as const) : []),
    // staff only: clients and Preview as client never see fuel (docs/specs/cloud/storage-uploads.md §6); the MR pages,
    // dockets and receipts read from a photo are here too (F03, F09; docs/specs/document-reading.md §6)
    ...(mode === 'staff' ? ([['dockets', 'Fuel and MR']] as const) : []),
    // staff only: who is on the project (decision 422); clients never see the crew
    ...(mode === 'staff' ? ([['crew', 'Crew']] as const) : []),
    // staff only: the project set-up wizard (batch 6, F13 and F26); clients never see it
    ...(mode === 'staff' ? ([['setup', 'Set-up']] as const) : []),
    // staff only: the final report's standard text of this project (decision 885); clients never see it
    ...(mode === 'staff' ? ([['final-report', 'Final report']] as const) : []),
  ];
  return (
    // the lines-remaining map and the Refly list take the whole width beside the menu (MB, 25 Oct 2026): the map grows
    // with the window and the list's columns need it
    <section
      className={
        tab === 'map' ||
        tab === 'refly' ||
        tab === 'diurnal' ||
        tab === 'flights' ||
        (tab === 'progress' && mode === 'staff')
          ? 'page page--wide'
          : 'page'
      }
    >
      <PageHeader
        title={`${project.number} ${project.title}`}
        subtitle={[
          project.area === project.title ? '' : project.area,
          mode === 'staff' ? (project.clientOrgName ?? 'No client organisation') : '',
        ]
          .filter(Boolean)
          .join(' · ')}
      >
        <Button variant="quiet" onClick={onBack}>
          All projects
        </Button>
      </PageHeader>
      {mode === 'staff' && (
        <div className="row row--wrap">
          <StatusControl project={project} roles={staffRoles} onChange={onStatus} labelled />
          {canPortal(staffRoles, 'project.link_client') && (
            <ClientOrgControl sb={sb} project={project} onChange={onClientOrg} />
          )}
          {canDeleteProject(staffRoles) && (
            <Button className="btn--danger" onClick={() => setDeleting(true)}>
              Delete project…
            </Button>
          )}
        </div>
      )}
      {mode === 'staff' && !project.clientOrgId && (
        <div className="notice" role="note" aria-label="No client organisation">
          <p className="body">
            No client organisation is linked to this project
            {project.clientName ? ` (Teslyn Field names the client "${project.clientName}")` : ''}. Until one
            is, the client portal and Preview as client show nothing of it: no report, refly list or photo,
            published or shared.{' '}
            {canPortal(staffRoles, 'project.link_client')
              ? 'Choose the organisation under "Client organisation" above; add it in Clients first if it is not there.'
              : 'A tenant admin, manager or office staff links it on this page.'}
          </p>
        </div>
      )}
      {statusError && (
        <div className="notice notice--fail" role="alert">
          <p className="body">{statusError}</p>
        </div>
      )}
      {complete && (
        <div className="notice" role="status" aria-label="Complete suggestion">
          <p className="body">Every line is accepted. Mark the project complete?</p>
          <div className="row">
            <Button variant="primary" onClick={() => onStatus('complete')}>
              Mark complete
            </Button>
          </div>
        </div>
      )}
      {deleting && (
        <DeleteDialog
          sb={sb}
          project={project}
          onCancel={() => setDeleting(false)}
          onDeleted={(text) => {
            setDeleting(false);
            onDeleted(text);
          }}
        />
      )}
      {mode === 'staff' && (onOpenClients || contract) && (
        <p className="row body">
          {onOpenClients && project.clientOrgName && (
            <button type="button" className="link" onClick={onOpenClients}>
              {project.clientOrgName} in Clients
            </button>
          )}
          {contract && onOpenContract && (
            <button type="button" className="link" onClick={() => onOpenContract(contract.id)}>
              Contract {contract.number}
            </button>
          )}
        </p>
      )}
      {/* the shared tab row (cloud-tabs, decision 1030): fades at a cut edge, a "Section" list on a phone */}
      <Tabs
        label={`${project.number} sections`}
        tabs={tabs}
        current={tab}
        onChange={setTab}
        idPrefix="project"
      />
      <div
        role="tabpanel"
        id={`project-panel-${tab}`}
        aria-labelledby={`project-tab-${tab}`}
        className="stack"
      >
        {tab === 'flights' && mode === 'staff' ? (
          <ProjectFlights sb={sb} project={project} />
        ) : tab === 'setup' && mode === 'staff' ? (
          <>
            {/* R209: the title, client name, area and state (the survey number read only) */}
            <ProjectDetailsPanel sb={sb} project={project} roles={roles ?? []} onSaved={onDetailsSaved} />
            <SetupWizard
              sb={sb}
              project={project}
              roles={roles ?? []}
              {...(onOpenPage ? { onOpenPage } : {})}
              {...(openablePages ? { openablePages } : {})}
            />
            {/* R161 (decision 974.5, 974.6): the project's crew check list and whether the client sees briefs */}
            <BriefSettingPanel sb={sb} project={project} roles={roles ?? []} />
          </>
        ) : tab === 'final-report' && mode === 'staff' ? (
          <FinalReportTextPanel
            sb={sb}
            tenantId={project.tenantId}
            tenantSlug={tenantSlug}
            projectId={project.id}
            roles={roles ?? []}
          />
        ) : tab === 'crew' && mode === 'staff' ? (
          <ProjectCrewPanel sb={sb} tenantId={project.tenantId} projectId={project.id} roles={roles ?? []} />
        ) : tab === 'dockets' && mode === 'staff' ? (
          <>
            <DocumentsPanel sb={sb} project={project} roles={roles ?? []} />
            <h2 className="heading">Fuel dockets</h2>
            <FuelDocketsList sb={sb} project={project} />
          </>
        ) : tab === 'refly' ? (
          <ReflyTab sb={sb} project={project} mode={mode} {...(roles ? { roles } : {})} />
        ) : tab === 'diurnal' ? (
          <DiurnalTab sb={sb} project={project} mode={mode} {...(roles ? { roles } : {})} />
        ) : tab === 'safety' ? (
          <SafetyTab sb={sb} project={project} mode={mode} {...(roles ? { roles } : {})} />
        ) : tab === 'approvals' ? (
          <ApprovalsTab
            sb={sb}
            project={project}
            mode={mode}
            {...(roles ? { roles } : {})}
            clientRole={clientRole}
            preview={preview}
          />
        ) : tab === 'photos' ? (
          <PhotosTab
            sb={sb}
            project={project}
            mode={mode}
            {...(roles ? { roles } : {})}
            {...(photoServices ? { services: photoServices } : {})}
          />
        ) : tab === 'reports' ? (
          <ReportsList
            sb={sb}
            mode={mode}
            projects={[project]}
            {...(roles ? { roles } : {})}
            {...(onReportOpened ? { onOpened: onReportOpened } : {})}
          />
        ) : (
          <>
            {error && <ErrorState message={error} onRetry={reload} />}
            {!plan && !error && <Loading what={mode === 'staff' ? 'project summary' : 'progress'} />}
            {plan && tab === 'progress' && mode === 'staff' && (
              <OverviewPanel sb={sb} project={project} plan={plan} onOpenFlights={() => setTab('flights')} />
            )}
            {plan && tab === 'progress' && mode !== 'staff' && <ProgressPanel plan={plan} />}
            {plan && tab === 'map' && (
              <MapPanel sb={sb} mode={mode} plan={plan} project={project} roles={roles ?? []} />
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Delete a project (tenant admins, decision 417): the number typed to confirm, and what will happen said first: no
 * field data, deleted at once; field data, 30 days in Deleted projects, restorable, then removed with its files.
 */
function DeleteDialog({
  sb,
  project,
  onCancel,
  onDeleted,
}: {
  readonly sb: ProjectsScreenProps['sb'];
  readonly project: PortalProject;
  readonly onCancel: () => void;
  readonly onDeleted: (notice: string) => void;
}): ReactElement {
  const [typed, setTyped] = useState('');
  const [fieldData, setFieldData] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    projectHasFieldData(sb, project.id)
      .then((v) => live && setFieldData(v))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, project.id]);
  const matches = deleteConfirmMatches(typed, project.number);
  const go = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await deleteProject(sb, project.id, typed);
      onDeleted(
        r.outcome === 'binned'
          ? `${project.number} is in the Deleted list (Projects, Show: Deleted) until ${lifecycleDateText(r.purge_after)}. A tenant admin can restore it until then.`
          : `${project.number} is deleted.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div className="panel confirm stack" role="dialog" aria-labelledby="delete-project-title">
      <h2 id="delete-project-title" className="heading">
        Delete {project.number}?
      </h2>
      {fieldData === null && !error && <Loading what="what the project holds" />}
      {fieldData === true && (
        <p className="body">
          {project.number} holds field data, so it goes to the Deleted list in Projects for 30 days: hidden
          everywhere, laptops stop syncing it, and a tenant admin can restore it. After 30 days it is removed
          for good with its reports, fuel dockets and archived files.
        </p>
      )}
      {fieldData === false && (
        <p className="body">
          {project.number} has no field data: it is deleted at once, with its blocks and planned lines. A
          contract keeps its record.
        </p>
      )}
      <TextField
        label="Type the project number to confirm"
        spellCheck={false}
        value={typed}
        onChange={setTyped}
        autoFocus
      />
      {error && (
        <p className="body field__error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button
          className="btn--danger"
          disabled={!matches || fieldData === null}
          busy={busy}
          onClick={() => void go()}
        >
          {busy ? 'Deleting…' : `Delete ${project.number}`}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const pct = (n: number): string => `${n} %`;

export function ProgressPanel({ plan }: { readonly plan: ProjectPlan }): ReactElement {
  const p = useMemo(() => projectProgress(plan.statuses, plan.plan, plan.blocks), [plan]);
  if (p.blocks.length === 0) {
    return (
      <div className="panel">
        <p className="body">No planned lines yet. Progress shows once the flight plan is in Teslyn.</p>
      </div>
    );
  }
  return (
    <div className="stack">
      <TableWrap>
        <table className="table progress-table" aria-label="Progress per block">
          <thead>
            <tr>
              <th className="label" scope="col">
                Block
              </th>
              <th className="label num" scope="col">
                Planned
              </th>
              <th className="label num" scope="col">
                Accepted
              </th>
              <th className="label num" scope="col">
                Remaining
              </th>
              <th className="label num" scope="col">
                Complete
              </th>
            </tr>
          </thead>
          <tbody>
            {p.blocks.map((b) => (
              <tr key={b.blockId}>
                <th scope="row" className="body">
                  {b.name}
                </th>
                <td className="data num">{kmText(b.plannedKm)}</td>
                <td className="data num">{kmText(b.acceptedKm)}</td>
                <td className="data num">{kmText(b.remainingKm)}</td>
                <td className="data num">{pct(b.percentComplete)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="progress-table__total">
              <th scope="row" className="body">
                Project
              </th>
              <td className="data num">{kmText(p.total.plannedKm)}</td>
              <td className="data num">{kmText(p.total.acceptedKm)}</td>
              <td className="data num">{kmText(p.total.remainingKm)}</td>
              <td className="data num">{pct(p.total.percentComplete)}</td>
            </tr>
          </tfoot>
        </table>
      </TableWrap>
      <p className="caption muted">
        From the reviewed line status. Accepted counts reviewed lines only; remaining is the planned km still
        to fly.
      </p>
    </div>
  );
}

/**
 * The lines-remaining legend in its order (the live map shows the same, docs/specs/cloud/live-tracking.md): the
 * states the map draws; a Completed line is not drawn (R136).
 */
export const LEGEND_ORDER: readonly LineState[] = REMAINING_MAP_STATES;

function Swatch({ entry }: { readonly entry: LegendEntry }): ReactElement {
  return (
    <span
      className={`legend__swatch legend__swatch--${entry.stroke}${entry.dashed ? ' legend__swatch--dashed' : ''}`}
      style={{ borderColor: `var(--${entry.colour})` }}
      aria-hidden="true"
    />
  );
}

/** Live photos for the map: every one for staff, the shared ones for a client (and Preview as client). */
function useMapPhotos(
  sb: SupabaseClient,
  projectId: string,
  mode: ReportsMode,
): { readonly photos: readonly PortalPhoto[]; readonly error: string | null } {
  const [photos, setPhotos] = useState<readonly PortalPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadPhotos(sb, projectId, mode)
      .then((rows) => live && setPhotos(mode === 'client' ? clientVisiblePhotos(rows) : livePhotos(rows)))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, projectId, mode]);
  return { photos, error };
}

const PHOTO_LEGEND_COLOUR = 'accent';

function MapPanel({
  sb,
  mode,
  plan,
  project,
  roles,
}: {
  readonly sb: SupabaseClient;
  readonly mode: ReportsMode;
  readonly plan: ProjectPlan;
  readonly project: PortalProject;
  readonly roles: readonly string[];
}): ReactElement {
  const { photos, error: photoError } = useMapPhotos(sb, project.id, mode);
  // the client portal map (parity C4): the project's aircraft and vehicles and published hazards, read as the client
  // reads them (Preview as client too); staff choose what it shows below the map
  const client = mode === 'client';
  const [shown, setShown] = useState<PortalMapShown>(PORTAL_MAP_SHOWN);
  // N16 (parity 03-031, decision 75): the replay of the last 48 hours, as Teslyn Field's and the staff live map's
  const [replaying, setReplaying] = useState(false);
  const live = usePortalMap(sb, project.id, client, replaying ? PORTAL_REPLAY_HOURS : shown.hours);
  const replay = useReplay(
    live.tracking?.aircraft ?? NO_AIRCRAFT,
    live.tracking?.trails ?? NO_TRAILS,
    PORTAL_REPLAY_HOURS,
    // opened once the 48 hours of positions are in, so it starts at their first fix
    client && replaying && live.trackingHours === PORTAL_REPLAY_HOURS,
  );
  // the client map draws no flight line under the aircraft: the list under the map has it (§11.1), once
  const clientTracking = useMemo<RemainingMapTracking | null>(() => {
    if (!shown.aircraft || !live.tracking) return null;
    if (replaying && !replay) return null;
    if (!replay) return { ...live.tracking, flightLines: false };
    if (!replay.frame) return null;
    return {
      aircraft: replay.frame.aircraft,
      trails: replay.frame.trails,
      colours: live.tracking.colours,
      flightLines: false,
    };
  }, [shown.aircraft, live.tracking, replay, replaying]);
  const hazards = useMemo(
    () =>
      (live.hazards?.layers ?? []).filter((l) =>
        l.kind === 'landholders' ? shown.landholders : shown.hazards,
      ),
    [live.hazards, shown.hazards, shown.landholders],
  );
  // fire on the map for staff only (decision 806): the client portal never asks
  const [fireChoice, setFireChoice] = useFireChoice();
  const fires = useCloudFires(sb, mode === 'staff' ? project.tenantId : null, fireChoice);
  // staff only: the organisation's own aircraft (decision 827) and the public air traffic around the project
  // (decision 765); a client and Preview as client never ask, their map keeps the client portal's rules
  const traffic = useProjectAircraft(sb, project, plan.blocks, mode === 'staff');
  // a public aircraft's planned route on a click (R160): the staff map only
  const routeOf = useMemo(() => cloudRouteFetcher(sb, project.tenantId), [sb, project.tenantId]);
  const r = useMemo(() => {
    const where = { epsg: project.epsg };
    const blocks = blockFeatures(withInfillKm(plan.blocks, plan.plan), where);
    // the staff map shows which way each patch and reflight is to be flown; the client portal's does not (decision 1011)
    const lines = linesRemaining(plan.statuses, plan.plan, plan.blocks, where, { arrows: mode === 'staff' });
    return {
      features: [...blocks.features, ...lines.features],
      arrows: lines.features.some((f) => f.style.symbol === 'arrow'),
      boundaries: blocks.features.length > 0,
      text: lines.text,
      notDrawn: [...blocks.notDrawn, ...lines.notDrawn],
      blocks: lines.blocks,
    };
  }, [plan, project.epsg, mode]);
  const pins = useMemo(() => photoFeatures(photos), [photos]);
  const features = useMemo(() => [...r.features, ...pins.features], [r.features, pins.features]);
  const unplaced = pins.notDrawn.length;
  return (
    // over Satellite with labels: the legend takes the imagery colours, as the map does (decision 654)
    <div className="stack" data-basemap="imagery">
      <p className="heading">{r.text}</p>
      {client && <PortalMapControls data={live} shown={shown} onChange={setShown} />}
      {client && (live.tracking?.aircraft.length ?? 0) > 0 && (
        <div className="row">
          <Button aria-pressed={replaying} onClick={() => setReplaying((v) => !v)}>
            {replaying ? 'Back to live' : 'Replay'}
          </Button>
        </div>
      )}
      {client && replaying && !replay && (
        <p className="caption muted" role="status">
          Getting the last 48 hours…
        </p>
      )}
      {client && replay && (
        <ReplayBar
          replay={replay}
          offsetH={viewerOffsetH()}
          note={PORTAL_REPLAY_NOTE}
          onClose={() => setReplaying(false)}
        />
      )}
      <RemainingMap
        features={features}
        label={`Map of ${project.number}. ${r.text}`}
        {...(client ? { hazards, tracking: clientTracking } : {})}
        {...(mode === 'staff'
          ? {
              overlay: fires.layers.features,
              credits: fires.layers.credits,
              tracking: traffic.tracking,
              routeOf,
            }
          : {})}
      />
      {mode === 'staff' && traffic.text && (
        <p className="caption muted" aria-label="Public air traffic">
          {traffic.text}
        </p>
      )}
      {mode === 'staff' && <FireControls fires={fires} choice={fireChoice} onChoice={setFireChoice} />}
      <ul className="legend" aria-label="Legend">
        {/* the survey boundary the map draws (decision 731) */}
        {r.boundaries && (
          <li className="legend__item caption">
            <Swatch entry={BOUNDARY_LEGEND} />
            {BOUNDARY_LEGEND.word}
          </li>
        )}
        {LEGEND_ORDER.map((s) => (
          <li key={s} className="legend__item caption">
            <Swatch entry={REMAINING_LEGEND[s]} />
            {REMAINING_LEGEND[s].word}
          </li>
        ))}
        {r.arrows && (
          <li className="legend__item caption">
            <SymbolGlyph symbol="arrow" colour={DIRECTION_ARROW_LEGEND.colour} />
            {DIRECTION_ARROW_LEGEND.word}
          </li>
        )}
        <li className="legend__item caption">
          <span
            className="legend__swatch legend__swatch--point"
            style={{
              backgroundColor: `var(--${PHOTO_LEGEND_COLOUR})`,
              borderColor: `var(--${PHOTO_LEGEND_COLOUR})`,
            }}
            aria-hidden="true"
          />
          Photo
        </li>
      </ul>
      {unplaced > 0 && (
        <p className="caption muted">
          {unplaced === 1
            ? '1 photo has no place recorded and is not on the map.'
            : `${unplaced} photos have no place recorded and are not on the map.`}
        </p>
      )}
      {photoError && (
        <p className="caption muted" role="status">
          Photos could not be loaded for the map: {photoError}
        </p>
      )}
      {client && <PortalTrackingList data={live} projectNumber={project.number} hours={shown.hours} />}
      {mode === 'staff' && <ClientMapSettings sb={sb} project={project} roles={roles} />}
      <ul className="stack" aria-label="Remaining per block">
        {r.blocks.map((b) => (
          <li key={b.block.id} className="body">
            {b.block.name}: <span className="data">{b.text}</span>
          </li>
        ))}
      </ul>
      {r.notDrawn.length > 0 && (
        <div className="notice notice--warn" role="status">
          <p className="label">Not drawn</p>
          <ul>
            {r.notDrawn.map((n) => (
              <li key={n} className="body">
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
